import React, { useState } from 'react';
import { Page } from '../../layout';
import { ReportInfoPanel } from '../../shared/components/ReportInfoPanel';
import { useQuery, useReactiveVar } from '@apollo/client';
import GET_BASELINE_REPORT from '../queries/getBaselineReportQuery';
import ApolloError from '../../shared/components/ApolloError';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { getPracticeAreaCharacterizationCSV } from '../reportDownloads/practiceAreaCharacterizationReport';
import { getAggregatePercentageCSV } from '../reportDownloads/aggregatePercentageReport';
import PracticeAreaGraphList from '../../shared/components/practiceAreaGraphList/PracticeAreaGraphList';
import { get } from 'lodash';
import IndustryReportsDescriptions from './IndustryReportsDescriptions';
import PageInstructions from '../../shared/components/PageInstructions';
import { Breadcrumb } from '../../shared/components/breadcrumb/Breadcrumb';
import { systemRole } from '../../../apollo/cache';
import { getAdjustedBreadcrumbPathItems } from '../../shared/services/breadcrumbService';
import { SelectedPracticeInformation } from '@shared/components/SelectedPracticeInformation';

const BaselineReportsPage = () => {
  const role = useReactiveVar(systemRole);
  const [selectedPracticeData, setSelectedPracticeData] = useState(null);

  const pageTitlte = 'Baseline';
  const breadcrumbPathItems = [
    { name: 'Data Reports', url: '/data-reports' },
    {
      name: 'Industry Reports',
      url: '/data-reports/industry-reports'
    },
    {
      name: pageTitlte,
      url: '/data-reports/industry-reports/baseline'
    }
  ];
  const adjustedBreadcrumbPathItems = getAdjustedBreadcrumbPathItems(breadcrumbPathItems, role.Id);

  const { data, loading, error } = useQuery(GET_BASELINE_REPORT);

  const handlePracticeClick = practice => {
    setSelectedPracticeData({
      practiceArea: {
        acronym: practice.Acronym
      },
      practice: {
        groupLevel: practice.Level,
        numberWithinLevel: practice.NumberInLevel,
        name: practice.PracticeName
      }
    });
  };

  const handleClickOutside = () => {
    setSelectedPracticeData(null);
  };

  const downloadReportLink = async () => {
    const zip = new JSZip();

    zip.file(
      'practice-area-characterization-report.csv',
      await getPracticeAreaCharacterizationCSV(data)
    );

    zip.file('aggregate-percentage-report.csv', await getAggregatePercentageCSV(data));

    zip.generateAsync({ type: 'blob' }).then(blob => {
      saveAs(blob, 'reports.zip');
    });
  };

  const downloadButton = (
    <div>
      <button className='primary-button' onClick={downloadReportLink}>
        Download Report
      </button>
    </div>
  );

  if (error) {
    return <ApolloError />;
  }

  const description =
    'A view of all Year 1 Baseline Appraisal data across Industry Participants in MDDAP, ' +
    'including the Practice Characterizations and Aggregate Percentages. Hover over a bar in any ' +
    'graph to see additional information. Click on any bar in a Practice Characterization table to see more details.';

  return (
    <Page title={pageTitlte}>
      <Breadcrumb pathItems={adjustedBreadcrumbPathItems}>{downloadButton}</Breadcrumb>

      <PageInstructions title='Baseline Industry Report' description={description} />
      <div className='practice-characterization--content' onClick={handleClickOutside}>
        <div className='practice-characterization--infobox'>
          <ReportInfoPanel panelName='report-info-panel__baseline'>
            <SelectedPracticeInformation selectedPracticeData={selectedPracticeData} />
          </ReportInfoPanel>
        </div>
        <div className='practice-characterization--graphs'>
          <IndustryReportsDescriptions />
          <PracticeAreaGraphList
            practiceAreas={get(data, 'baselineReport.PracticeAreas', [])}
            handlePracticeClick={handlePracticeClick}
            loading={loading}
          />
        </div>
      </div>
    </Page>
  );
};

export default BaselineReportsPage;
